import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { ErpModule } from '@/api/platform.api';
import { approvalsApi } from '@/api/approvals.api';
import { legacyWorkRedirectTarget, moduleApprovalsPath, moduleWorkPath } from '@/config/moduleWorkPaths';
import { useActiveModule } from '@/hooks/useActiveModule';
import { useAppSelector } from '@/hooks/useAppSelector';
import { parseApprovalOwnerModule } from '@/utils/approvalDeepLink';

/**
 * Legacy `/work/approvals?instance=` links used to redirect by session module (often POS).
 * Resolve the owning module from the instance so email deep links open the correct inbox.
 */
export function ApprovalLegacyDeepLinkRedirect({ fallbackModule = 'POS' }: { fallbackModule?: ErpModule }) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeModule = useActiveModule();
  const entryModule = useAppSelector((s) => s.auth.entryModule);
  const sessionModule = activeModule ?? entryModule ?? fallbackModule;
  const instanceParam = new URLSearchParams(location.search).get('instance');
  const [fallbackReady, setFallbackReady] = useState(!instanceParam);

  useEffect(() => {
    if (!instanceParam) {
      setFallbackReady(true);
      return;
    }
    const id = Number(instanceParam);
    if (!Number.isFinite(id) || id <= 0) {
      setFallbackReady(true);
      return;
    }
    let cancelled = false;
    void approvalsApi
      .getInstance(id)
      .then((instance) => {
        if (cancelled) return;
        const owner = parseApprovalOwnerModule(instance.ownerModule) ?? sessionModule;
        const params = new URLSearchParams(location.search);
        navigate(`${moduleApprovalsPath(owner)}?${params.toString()}`, { replace: true });
      })
      .catch(() => {
        if (!cancelled) setFallbackReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [instanceParam, location.search, navigate, sessionModule]);

  if (!fallbackReady) {
    return null;
  }

  const target = legacyWorkRedirectTarget(location.pathname, sessionModule) ?? moduleWorkPath(sessionModule);
  return <Navigate to={`${target}${location.search}`} replace />;
}
